import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {firstValueFrom, forkJoin} from 'rxjs';
import { first } from 'rxjs/operators';

import { UICollectionService } from '@sim/collections/services';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, filePath, SwalService, UIExportService } from '@atmi/core';
import { CardMI } from '@atmi/core/models';
import { SettingsApiService } from '@atmi/core/services/settings-api.service';
import { IndexQueryStringType } from '@atmi/core/utils';
import { CardMiFiltersComponent } from '@atmi/shared/filters/card-mi-filters/card-mi-filters.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-cards-mi',
  styleUrls: ['./cards-mi.component.scss'],
  templateUrl: './cards-mi.component.html',
})
export class CardsMiComponent extends BaseTableComponent<CardMI> implements UiTableComponent<CardMI> {
  readonly MobileFiltersComponent = CardMiFiltersComponent;

  readonly fields: TableField<CardMI>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'SGT.SGTT',
      sortable: true,
    },
    {
      context: (x) => x.DT.length ? x.DT[0].DTS.DTSI : '-',
      header: this.translate.instant('Anno'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'DT.DTS.DTSI',
      sortable: true,
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  currentFilters: IndexQueryStringType = {};

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => {
    sort = sort && sort.length > 0 ? sort : [{ field: 'SGT.SGTT', type: 'asc' }];
    this.currentFilters = { sort, filter };
    return this.cardsApi.index('mi', { sort, page, limit, filter });
  });

  readonly contextMenus: Array<TableContextMenu<CardMI>> = [];

  readonly isExportEnable = this.settingsApi.selectStateSnapshot().system?.layouts.some((l) => l.className === 'BeneMI');

  defaultLimit = 300;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cardsApi: CardsApiService,
    private readonly roleService: RoleService,
    private readonly settingsApi: SettingsApiService,
    private readonly uiExportService: UIExportService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    readonly uiCollectionService: UICollectionService,
  ) {
    super(translate, router, route, swalService, 'mi/manager');

    if (this.roleService.hasCapability('BeneMI', ['update'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    } else if (this.roleService.hasCapability('BeneMI', ['read', 'read+all'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Visualizza'),
      });
      this.fields.push(basicShowTemplateOptions);
    }

    if (this.roleService.hasCapability('BeneMI', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.cardsApi.removeMI(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => this.cardsApi.removeMI(id));

    return forkJoin(deleteApis);
  }

  async addToCollectionHandler() {
    const success = await this.uiCollectionService.showCollectionPickerHandler(this.selectedItemIds, 'BeneMI');
    if (success) {
      this.selectedItems = [];
    }
  }

  applyFilterWithDelayHandler(_: any) {
  }

  async exportHandler() {
    this.uiExportService.manageExport(
      async (template?: string, format?: string) => {
        const selection = this.selectedItemIds.length ? this.selectedItemIds : undefined;
        const blob = await firstValueFrom(this.cardsApi.exportList('mi', template, format, this.currentFilters, selection).pipe(first()));
        this.selectedItems = [];
        return blob;
      },
      'BeneMI',
      `beni-mi`,
    );
  }
}
